import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { RootState } from '../../../../../../store/store';
import type { Product } from '../../../../../../types/common';
import ProductCard, { type Discount, type ImagesObject, type ProductCardData } from '../../../../../shared/ProductCard/ProductCard';
import styles from './favoritesPanel.module.scss';

const isPositiveNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;

const toImages = (p: Product): ImagesObject => ({
    image: p.image ?? '',
    secondImage: p.secondImage ?? '',
    thirdImage: p.thirdImage ?? '',
    fourthImage: p.fourthImage ?? '',
    fifthImage: p.fifthImage ?? '',
    sixthImage: p.sixthImage ?? '',
});

const computeDiscount = (p: Product): Discount => {
    const basePrice = isPositiveNumber(p.price) ? p.price : null;
    const raw = (p as any)?.specialDiscount ?? (p as any)?.discount;
    const discounted = raw && isPositiveNumber(raw.discountedPrice) ? raw.discountedPrice : null;
    if (!basePrice || !discounted || discounted >= basePrice) return null;
    const percent = Math.max(0, Math.min(100, Math.round(((basePrice - discounted) / basePrice) * 100)));
    return {
        percent,
        discountedPrice: discounted,
    };
};

const isPromoKey = (v: unknown): v is 'promo' | 'season' | 'sale' | 'blank' => v === 'promo' || v === 'season' || v === 'sale' || v === 'blank';

const normalizePromoType = (p: Product): string => {
    const candidate = (p as any)?.promoType;
    return isPromoKey(candidate) ? candidate : 'blank';
};

const mapProductToCardData = (p: Product): ProductCardData => ({
    _id: p.id ?? '',
    category: {
        id: p.category?.id ?? '',
        name: p.category?.name ?? '',
        url: p.category?.url ?? '',
    },
    discount: computeDiscount(p),
    name: p.name ?? '',
    price: isPositiveNumber(p.price) ? p.price : 0,
    weight: isPositiveNumber((p as any)?.weight) ? (p as any).weight : 0,
    fillerType: {
        id: p.fillerType?.id ?? '',
        name: p.fillerType?.name ?? '',
        url: p.fillerType?.url ?? '',
    },
    fabricType: {
        id: p.fabricType?.id ?? '',
        name: p.fabricType?.name ?? '',
        url: p.fabricType?.url ?? '',
    },
    articleNumber: (p as any)?.articleNumber ?? '',
    brandName: (p as any)?.brandName ?? '',
    nameDescription: (p as any)?.nameDescription ?? '',
    priority: isPositiveNumber((p as any)?.priority) ? (p as any).priority : 0,
    images: toImages(p),
    group: {
        id: p.group?.id ?? '',
        name: p.group?.name ?? '',
        url: p.group?.url ?? '',
        optionValue: p.group?.optionValue ?? '',
    },
    slug: p.slug ?? '',
    season: (p as any)?.season ?? null,
    promoType: normalizePromoType(p),
});

const FavoritesPanel = () => {
    const navigate = useNavigate();
    const { products } = useSelector((state: RootState) => state.product);
    const { wishlist } = useSelector((state: RootState) => state.user);

    const wishlistProducts = useMemo(() => products.filter((product) => wishlist.includes(product.id)), [products, wishlist]);
    const favoriteCards = useMemo(() => wishlistProducts.map(mapProductToCardData), [wishlistProducts]);

    const hasFavorites = favoriteCards.length > 0;

    return (
        <section className={styles.wrapper} aria-label="Избранные товары">
            <div className={styles.header}>
                <h2 className={styles.title}>Избранное</h2>
                {favoriteCards.length > 0 && <span className={styles.count}>{favoriteCards.length} шт</span>}
            </div>

            {hasFavorites ? (
                <div className={styles.favoritesWrap}>
                    {favoriteCards.map((product) => (
                        <ProductCard key={`favorite-${product._id}`} data={product} />
                    ))}
                </div>
            ) : (
                <div className={styles.empty}>
                    <p className={styles.emptyTitle}>
                        Вы пока ничего <br className={styles.emptyTitleBr} />
                        не выбрали
                    </p>
                    <p className={styles.emptyText}>Давайте что-нибудь подберем из каталога.</p>
                </div>
                // <div className={styles.emptyTwo}>
                //     <p className={styles.emptyTwoTitle}>Вы пока ничего не выбрали</p>
                //     <p className={styles.emptyTwoText}>Давайте что-нибудь подберем из каталога</p>
                // </div>
            )}

            <button type="button" className={styles.catalogBtn} onClick={() => navigate('/catalog')}>
                В каталог
            </button>
        </section>
    );
};

export default FavoritesPanel;
